export interface ColumnDef<T> {
  key: keyof T | string;
  label: string;
  align?: 'left' | 'center' | 'right';
  format?: (v: unknown, row: T) => string;
  sortable?: boolean;
  width?: string;
}

/**
 * Tableau de données générique avec tri, recherche, pagination.
 * Usage TypeScript : new DataTable<Salarie>(...).mount(container)
 */
export class DataTable<T extends object> {
  private currentPage = 1;
  private pageSize = 25;
  private sortKey: string | null = null;
  private sortDir: 'asc' | 'desc' = 'asc';
  private search = '';

  constructor(
    private columns: readonly ColumnDef<T>[],
    private data: readonly T[],
    private opts: { emptyLabel?: string; rowClick?: (row: T) => void; caption?: string } = {},
  ) {}

  mount(container: HTMLElement): void {
    container.innerHTML = '';
    container.appendChild(this.build());
  }

  private build(): HTMLElement {
    const wrap = document.createElement('div');
    const caption = this.opts.caption ?? 'Tableau de données';
    const captionSafe = caption.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    wrap.innerHTML = `
      <div style="display:flex;gap:8px;margin-bottom:8px;">
        <input type="search" placeholder="Rechercher…" style="flex:1;padding:6px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;" />
      </div>
      <div style="overflow:auto;border:1px solid #e1e4e8;border-radius:8px;background:white;">
        <table class="data" style="width:100%;border-collapse:collapse;font-size:0.85rem;" aria-label="${captionSafe}">
          <caption class="sr-only">${captionSafe}</caption>
          <thead><tr>${this.columns.map((c) => `<th scope="col" data-key="${String(c.key)}" style="padding:10px 12px;background:#f6f8fa;border-bottom:1px solid #e1e4e8;text-align:${c.align ?? 'left'};cursor:${c.sortable ? 'pointer' : 'default'};">${c.label}${c.sortable ? ' ⇅' : ''}</th>`).join('')}</tr></thead>
          <tbody></tbody>
        </table>
      </div>
      <div class="paginator" style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:0.85rem;color:#586069;"></div>
    `;
    const tbody = wrap.querySelector('tbody')!;
    const input = wrap.querySelector('input')!;
    input.addEventListener('input', () => {
      this.search = input.value.toLowerCase();
      this.currentPage = 1;
      this.refresh(tbody, wrap.querySelector('.paginator')!);
    });
    wrap.querySelectorAll<HTMLTableCellElement>('th[data-key]').forEach((th) => {
      const col = this.columns.find((c) => String(c.key) === th.dataset['key']);
      if (col?.sortable)
        th.addEventListener('click', () =>
          this.toggleSort(String(col.key), tbody, wrap.querySelector('.paginator')!),
        );
    });
    this.refresh(tbody, wrap.querySelector('.paginator')!);
    return wrap;
  }

  private refresh(tbody: HTMLElement, paginator: HTMLElement): void {
    const filtered = this.search
      ? this.data.filter((r) => JSON.stringify(r).toLowerCase().includes(this.search))
      : this.data;
    const sorted = this.sortKey ? this.sortBy(filtered, this.sortKey, this.sortDir) : filtered;
    const start = (this.currentPage - 1) * this.pageSize;
    const page = sorted.slice(start, start + this.pageSize);
    tbody.innerHTML =
      page.length === 0
        ? `<tr><td colspan="${this.columns.length}" style="padding:24px;text-align:center;color:#586069;">${this.opts.emptyLabel ?? 'Aucune donnée'}</td></tr>`
        : page
            .map(
              (row) =>
                `<tr style="cursor:${this.opts.rowClick ? 'pointer' : 'default'};">${this.columns
                  .map((c) => {
                    const val = this.cellValue(row, c);
                    return `<td style="padding:8px 12px;border-bottom:1px solid #f3f5f7;text-align:${c.align ?? 'left'};">${val}</td>`;
                  })
                  .join('')}</tr>`,
            )
            .join('');
    if (this.opts.rowClick) {
      Array.from(tbody.querySelectorAll('tr')).forEach((tr, idx) => {
        tr.addEventListener('click', () => this.opts.rowClick?.(page[idx]!));
      });
    }
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / this.pageSize));
    paginator.innerHTML = `<span>${total} résultat${total > 1 ? 's' : ''}</span>
      <span>Page <button data-act="prev" ${this.currentPage <= 1 ? 'disabled' : ''}>‹</button> ${this.currentPage} / ${totalPages} <button data-act="next" ${this.currentPage >= totalPages ? 'disabled' : ''}>›</button></span>`;
    paginator.querySelector('[data-act="prev"]')?.addEventListener('click', () => {
      this.currentPage--;
      this.refresh(tbody, paginator);
    });
    paginator.querySelector('[data-act="next"]')?.addEventListener('click', () => {
      this.currentPage++;
      this.refresh(tbody, paginator);
    });
  }

  private cellValue(row: T, col: ColumnDef<T>): string {
    const raw = (row as Record<string, unknown>)[String(col.key)];
    if (col.format) return col.format(raw, row);
    return raw === undefined || raw === null ? '' : String(raw);
  }

  private sortBy(rows: readonly T[], key: string, dir: 'asc' | 'desc'): T[] {
    return rows.slice().sort((a, b) => {
      const va = (a as Record<string, unknown>)[key];
      const vb = (b as Record<string, unknown>)[key];
      if (typeof va === 'number' && typeof vb === 'number')
        return dir === 'asc' ? va - vb : vb - va;
      return dir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }

  private toggleSort(key: string, tbody: HTMLElement, paginator: HTMLElement): void {
    if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.refresh(tbody, paginator);
  }
}
